import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import GoogleSignInButton from '../../components/GoogleSignInButton/GoogleSignInButton';
import Header from '../../components/Header/Header';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSmallTextSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';
import { cacheCurrentListId } from '../../utils/listCache';
import { trackAuthEvent } from '../../utils/analytics';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { fontSize } = useFontSize();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  // Set page title and scroll to top
  useEffect(() => {
    document.title = 'Stock Table - Sign Up';
    window.scrollTo(0, 0);
  }, []);

  // Check if user is coming from a Save action, New List creation, or Sign Up button
  const isFromSaveAction = location.state?.fromSave === true;
  const isFromNewList = location.state?.fromNewList === true;
  const isFromSignUp = location.state?.fromSignUp === true;
  const returnPath = location.state?.returnPath;
  const isFromPublicList = location.state?.isFromPublicList === true;
  const fromFreeTrialIntent = location.state?.fromFreeTrialIntent === true;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): boolean => {
    // At least 8 characters, one letter, one number
    const hasMinLength = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    return hasMinLength && hasLetter && hasNumber;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors = {
      email: '',
      password: ''
    };

    // Validate email
    if (!formData.email) {
      newErrors.email = 'Email address is required.';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required.';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long with at least one letter and one number.';
    }

    setErrors(newErrors);

    // If no errors, proceed with form submission
    if (!newErrors.email && !newErrors.password) {
      setIsLoading(true);
      
      try {
        const response = await fetch('/api/v1/auth/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Track signup event
          trackAuthEvent('signup');
          
          // Redirect to verification page with email and return path if from save action or new list
          navigate('/verify-email', { 
            state: { 
              email: formData.email,
              fromSave: isFromSaveAction,
              fromNewList: isFromNewList,
              fromSignUp: isFromSignUp,
              returnPath: returnPath,
              isFromPublicList: isFromPublicList,
              fromFreeTrialIntent: fromFreeTrialIntent
            },
            replace: true 
          });
        } else {
          // Handle registration errors
          if (data.message && data.message.includes('already exists')) {
            setErrors(prev => ({
              ...prev,
              email: 'An account with this email already exists.'
            }));
          } else {
            setErrors(prev => ({
              ...prev,
              email: data.message || 'Registration failed. Please try again.'
            }));
          }
        }
      } catch (error) {
        setErrors(prev => ({
          ...prev,
          email: 'Network error. Please check your connection and try again.'
        }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setIsGoogleLoading(true);
    
    try {
      const response = await fetch('/api/v1/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credential,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Always check for session data or create default list after Google sign-in
        const tickerListStr = sessionStorage.getItem('stocktable-ticker-list');
        const listName = sessionStorage.getItem('stocktable-list-name');
        const hiddenStatsStr = sessionStorage.getItem('stocktable-hidden-statistics');
        
        let tickerList = null;
        let hiddenStatistics = null;
        
        if (tickerListStr) {
          try {
            tickerList = JSON.parse(tickerListStr);
            if (!Array.isArray(tickerList) || tickerList.length === 0) {
              tickerList = null;
            }
          } catch (e) {
            console.error('Failed to parse ticker list:', e);
          }
        }
        
        if (hiddenStatsStr) {
          try {
            hiddenStatistics = JSON.parse(hiddenStatsStr);
          } catch (e) {
            console.error('Failed to parse hidden statistics:', e);
          }
        }
        
        // Use default list name if missing
        const finalListName = listName || 'Main List';
        
        const sessionData = {
          tickerList,
          listName: finalListName,
          hiddenStatistics
        };
        
        // Pass locationState so saveSessionDataToAccount knows the context
        const locationState = {
          fromSave: isFromSaveAction,
          fromNewList: isFromNewList,
          fromSignUp: isFromSignUp,
          returnPath: returnPath,
          isFromPublicList: isFromPublicList,
          fromFreeTrialIntent: fromFreeTrialIntent
        };
        
        // If user came from a save action, new list creation, or sign up button, save their session data
        if (isFromSaveAction || isFromNewList || isFromSignUp) {
          if (sessionData.tickerList && sessionData.tickerList.length > 0) {
            await saveSessionDataToAccount(data.accessToken, sessionData, locationState);
          } else {
            // No session data to save - create default "Main List" if user has no lists
            const { createDefaultMainListIfNeeded } = await import('../../utils/sessionDataUtils');
            await createDefaultMainListIfNeeded(data.accessToken);
          }
        } else {
          // User came directly to sign up (not from save action) - create default list if no session data
          if (sessionData.tickerList && sessionData.tickerList.length > 0) {
            await saveSessionDataToAccount(data.accessToken, sessionData, locationState);
          } else {
            console.log('⚠️ No session data - creating default "Main List" for new user');
            const { createDefaultMainListIfNeeded } = await import('../../utils/sessionDataUtils');
            await createDefaultMainListIfNeeded(data.accessToken);
          }
        }
        
        // Login the user using the auth context
        login(data.accessToken, data.user, data.refreshToken);
        
        // Redirect to the appropriate page
        // Pass fromGetStarted so list pages show "Start here!" tooltip for new users with empty list
        const showGetStartedTooltip = { state: { fromGetStarted: true } };
        // If coming from "Start Free Trial Now" and user logged into existing account (Google), go to daily not order-summary
        if (fromFreeTrialIntent && data.isNewUser === false) {
          navigate('/daily', showGetStartedTooltip);
        } else if (fromFreeTrialIntent && data.isNewUser === true) {
          navigate('/order-summary', { state: { freeTrialEligible: true } });
        } else if (isFromPublicList) {
          navigate('/daily', showGetStartedTooltip);
        } else if (isFromSaveAction && returnPath) {
          navigate(returnPath, showGetStartedTooltip);
        } else if (isFromNewList && returnPath) {
          navigate(returnPath, showGetStartedTooltip);
        } else if (isFromSignUp && returnPath) {
          navigate(returnPath, showGetStartedTooltip);
        } else {
          navigate('/daily', showGetStartedTooltip);
        }
      } else {
        setErrors(prev => ({
          ...prev,
          email: data.message || 'Google sign-in failed. Please try again.'
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        email: 'Network error. Please check your connection and try again.'
      }));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setErrors(prev => ({
      ...prev,
      email: 'Google sign-in was cancelled or failed. Please try again.'
    }));
  };

  // Function to save session data to user account
  const saveSessionDataToAccount = async (accessToken: string, sessionData?: any, locationState?: any) => {
    try {
      console.log('🔍 saveSessionDataToAccount called with flags:', {
        isFromSaveAction,
        isFromNewList,
        isFromSignUp,
        returnPath
      });
      
      if (isFromNewList) {
        // For new list creation, save the user's current Main List with all their data
        console.log('Creating new list for user after signup with their current data');
        
        // Use provided session data or get from sessionStorage
        const tickerList = sessionData?.tickerList || sessionStorage.getItem('stocktable-ticker-list');
        const listName = sessionData?.listName || sessionStorage.getItem('stocktable-list-name');
        const hiddenStatistics = sessionData?.hiddenStatistics || sessionStorage.getItem('stocktable-hidden-statistics');
        
        if (!tickerList || !listName) {
          console.log('No session data to save for new list creation');
          return;
        }

        const tickers = Array.isArray(tickerList) ? tickerList : JSON.parse(tickerList);
        // Handle both old flat array structure and new categorized structure
        let hiddenStats;
        if (Array.isArray(hiddenStatistics)) {
          // Old flat array structure
          hiddenStats = hiddenStatistics;
        } else if (typeof hiddenStatistics === 'string') {
          // JSON string that needs parsing
          hiddenStats = JSON.parse(hiddenStatistics);
        } else if (typeof hiddenStatistics === 'object' && hiddenStatistics !== null) {
          // New categorized object structure
          hiddenStats = hiddenStatistics;
        } else {
          // Fallback to empty object
          hiddenStats = {};
        }
        
        console.log('Saving Main List data for new user:', {
          listName,
          tickers,
          tickerCount: tickers.length,
          tickerOrder: tickers.map((ticker: string, index: number) => ({ ticker, order: index + 1 })),
          hiddenStatistics: hiddenStats
        });
        
        // Create the list with the user's current list name
        const createListResponse = await fetch('/api/v1/user-ticker-list/create_list', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            list_name: listName
          }),
        });

        if (!createListResponse.ok) {
          throw new Error('Failed to create new list');
        }

        const createListData = await createListResponse.json();
        const listId = createListData.data.id;
        
        // Cache the new list ID so the user gets redirected to the correct list
        cacheCurrentListId(listId);
        console.log('📦 Cached new list ID:', listId);

        // Add each ticker to the list with proper order
        for (let i = 0; i < tickers.length; i++) {
          const ticker = tickers[i];
          const orderNum = i + 1; // 1-based ordering
          
          try {
            const response = await fetch(`/api/v1/user-ticker-list/${listId}/tickers`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                ticker_symbol: ticker,
                order_num: orderNum
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, errorData);
            } else {
              console.log(`Successfully added ticker ${ticker} to list at position ${orderNum}`);
            }
          } catch (error) {
            console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, error);
            // Continue with other tickers even if one fails
          }
        }

        // Save list preferences if they exist
        const hasPreferences = hiddenStats && (
          (Array.isArray(hiddenStats) && hiddenStats.length > 0) ||
          (typeof hiddenStats === 'object' && (
            (hiddenStats.daily && hiddenStats.daily.length > 0) ||
            (hiddenStats.technicals && hiddenStats.technicals.length > 0) ||
            (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) ||
            (hiddenStats.analystViews && hiddenStats.analystViews.length > 0)
          ))
        );
        
        if (hasPreferences) {
          try {
            console.log('Saving list preferences for new user:', hiddenStats);
            
            // Use the new categorized structure - indicators are already categorized by page type
            const preferencesData: any = {
              userTickerListId: listId
            };
            
            // Set the appropriate fields based on the categorized structure
            if (hiddenStats.daily && hiddenStats.daily.length > 0) {
              preferencesData.dailyIndicatorsToHide = hiddenStats.daily;
            }
            if (hiddenStats.technicals && hiddenStats.technicals.length > 0) {
              preferencesData.technicalIndicatorsToHide = hiddenStats.technicals;
            }
            if (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) {
              preferencesData.fundamentalIndicatorsToHide = hiddenStats.fundamentals;
            }
            if (hiddenStats.analystViews && hiddenStats.analystViews.length > 0) {
              preferencesData.analystIndicatorsToHide = hiddenStats.analystViews;
            }
            
            const preferencesResponse = await fetch('/api/v1/user-ticker-list/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(preferencesData),
            });

            if (!preferencesResponse.ok) {
              console.error('Failed to save list preferences:', await preferencesResponse.text());
            } else {
              console.log('Successfully saved list preferences for new user');
            }
          } catch (error) {
            console.error('Failed to save list preferences:', error);
          }
        }

        // Save individual ticker preferences
        await saveTickerPreferences(accessToken, tickers);

        // Clear session data after successful save
        sessionStorage.removeItem('stocktable-ticker-list');
        sessionStorage.removeItem('stocktable-list-name');
        sessionStorage.removeItem('stocktable-hidden-statistics');
        
        // Clear individual ticker preferences from sessionStorage
        clearTickerPreferencesFromSession(tickers);
        
        console.log('Main List created successfully for new user:', {
          listId,
          listName,
          tickersProcessed: tickers.length,
          preferencesSaved: hasPreferences
        });
        
        // Now create a new empty list for the user to work with
        console.log('Creating new empty list for user to work with...');
        
        const newListResponse = await fetch('/api/v1/user-ticker-list/create_list', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            list_name: 'New List'
          }),
        });

        if (!newListResponse.ok) {
          console.error('Failed to create new empty list');
        } else {
          const newListData = await newListResponse.json();
          const newListId = newListData.data.id;
          
          // Cache the new list ID so the user gets redirected to the new empty list
          cacheCurrentListId(newListId);
          console.log('📦 Cached new empty list ID:', newListId);
        }
        
        return;
      }
      
      // For save action or sign up, save the current session data
      console.log('🔍 Executing save action/sign up logic');
      const tickerList = sessionData?.tickerList || sessionStorage.getItem('stocktable-ticker-list');
      let listName = sessionData?.listName || sessionStorage.getItem('stocktable-list-name');
      const hiddenStatistics = sessionData?.hiddenStatistics || sessionStorage.getItem('stocktable-hidden-statistics');
      
      // Use default list name if missing
      if (!listName) {
        listName = 'Main List';
      }
      
      // Only require tickerList - listName will use default if missing
      if (!tickerList) {
        return;
      }

      const tickers = Array.isArray(tickerList) ? tickerList : JSON.parse(tickerList);
      // Handle both old flat array structure and new categorized structure
      let hiddenStats;
      if (Array.isArray(hiddenStatistics)) {
        // Old flat array structure
        hiddenStats = hiddenStatistics;
      } else if (typeof hiddenStatistics === 'string') {
        // JSON string that needs parsing
        hiddenStats = JSON.parse(hiddenStatistics);
      } else if (typeof hiddenStatistics === 'object' && hiddenStatistics !== null) {
        // New categorized object structure
        hiddenStats = hiddenStatistics;
      } else {
        // Fallback to empty object
        hiddenStats = {};
      }
      
      console.log('Saving session data to account:', {
        listName,
        tickers,
        tickerCount: tickers.length,
        tickerOrder: tickers.map((ticker: string, index: number) => ({ ticker, order: index + 1 })),
        hiddenStatistics: hiddenStats
      });
      
      // Create the list
      const createListResponse = await fetch('/api/v1/user-ticker-list/create_list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          list_name: listName
        }),
      });

      if (!createListResponse.ok) {
        throw new Error('Failed to create list');
      }

      const createListData = await createListResponse.json();
      const listId = createListData.data.id;

      // Add each ticker to the list with proper order
      for (let i = 0; i < tickers.length; i++) {
        const ticker = tickers[i];
        const orderNum = i + 1; // 1-based ordering
        
        try {
          const response = await fetch(`/api/v1/user-ticker-list/${listId}/tickers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              ticker_symbol: ticker,
              order_num: orderNum
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, errorData);
          } else {
            console.log(`Successfully added ticker ${ticker} to list at position ${orderNum}`);
          }
        } catch (error) {
          console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, error);
          // Continue with other tickers even if one fails
        }
      }

      // Save list preferences if they exist
      const hasPreferences = hiddenStats && (
        (Array.isArray(hiddenStats) && hiddenStats.length > 0) ||
        (typeof hiddenStats === 'object' && (
          (hiddenStats.daily && hiddenStats.daily.length > 0) ||
          (hiddenStats.technicals && hiddenStats.technicals.length > 0) ||
          (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) ||
          (hiddenStats.analystViews && hiddenStats.analystViews.length > 0)
        ))
      );
      
      if (hasPreferences) {
        try {
          console.log('Saving list preferences:', hiddenStats);
          
          // Use the new categorized structure - indicators are already categorized by page type
          const preferencesData: any = {
            userTickerListId: listId
          };
          
          // Set the appropriate fields based on the categorized structure
          if (hiddenStats.daily && hiddenStats.daily.length > 0) {
            preferencesData.dailyIndicatorsToHide = hiddenStats.daily;
          }
          if (hiddenStats.technicals && hiddenStats.technicals.length > 0) {
            preferencesData.technicalIndicatorsToHide = hiddenStats.technicals;
          }
          if (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) {
            preferencesData.fundamentalIndicatorsToHide = hiddenStats.fundamentals;
          }
          if (hiddenStats.analystViews && hiddenStats.analystViews.length > 0) {
            preferencesData.analystIndicatorsToHide = hiddenStats.analystViews;
          }
          
          const preferencesResponse = await fetch('/api/v1/user-ticker-list/preferences', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify(preferencesData),
          });

          if (!preferencesResponse.ok) {
            console.error('Failed to save list preferences:', await preferencesResponse.text());
          } else {
            console.log('Successfully saved list preferences');
          }
        } catch (error) {
          console.error('Failed to save list preferences:', error);
        }
      }

      // Save individual ticker preferences
      await saveTickerPreferences(accessToken, tickers);

      // Clear session data after successful save
      sessionStorage.removeItem('stocktable-ticker-list');
      sessionStorage.removeItem('stocktable-list-name');
      sessionStorage.removeItem('stocktable-hidden-statistics');
      
      // Clear individual ticker preferences from sessionStorage
      clearTickerPreferencesFromSession(tickers);
      
      console.log('Session data saved to account successfully:', {
        listId,
        listName,
        tickersProcessed: tickers.length,
        preferencesSaved: hasPreferences
      });
    } catch (error) {
      console.error('Failed to save session data to account:', error);
      // Don't throw error to avoid breaking the signup flow
    }
  };

  // Helper function to save individual ticker preferences
  const saveTickerPreferences = async (accessToken: string, tickers: string[]) => {
    for (const ticker of tickers) {
      try {
        // Get ticker basics ID first
        const syncResponse = await fetch('/api/v1/ticker/check-sync-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            ticker_symbols: [ticker]
          }),
        });

        if (!syncResponse.ok) {
          console.error(`Failed to get sync status for ${ticker}`);
          continue;
        }

        const syncData = await syncResponse.json();
        const tickerInfo = syncData.data.tickers.find((t: any) => t.ticker_symbol === ticker);
        
        if (!tickerInfo) {
          console.error(`Ticker ${ticker} not found in sync response`);
          continue;
        }

        const tickerBasicsId = tickerInfo.ticker_basics_id;
        
        // Check for ticker-specific preferences in sessionStorage
        const technicalHiddenStats = sessionStorage.getItem(`stocktable-ticker-technicals-hidden-statistics-${ticker}`);
        const technicalHiddenColumns = sessionStorage.getItem(`stocktable-ticker-technicals-hidden-columns-${ticker}`);
        const fundamentalHiddenStats = sessionStorage.getItem(`stocktable-ticker-fundamentals-hidden-statistics-${ticker}`);
        const analystHiddenStats = sessionStorage.getItem(`stocktable-ticker-analyst-views-hidden-statistics-${ticker}`);

        // Save technical preferences if they exist
        if (technicalHiddenStats || technicalHiddenColumns) {
          const technicalStats = technicalHiddenStats ? JSON.parse(technicalHiddenStats) : [];
          const technicalColumns = technicalHiddenColumns ? JSON.parse(technicalHiddenColumns) : [];
          
          if (technicalStats.length > 0 || technicalColumns.length > 0) {
            const technicalPreferences = {
              tickerBasicsId,
              technicalIndicatorsToHide: technicalStats,
              technicalDaysAgoToHide: technicalColumns
            };

            const response = await fetch('/api/v1/user-ticker/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(technicalPreferences),
            });

            if (!response.ok) {
              console.error(`Failed to save technical preferences for ${ticker}:`, await response.text());
            } else {
              console.log(`Successfully saved technical preferences for ${ticker}`);
            }
          }
        }

        // Save fundamental preferences if they exist
        if (fundamentalHiddenStats) {
          const fundamentalStats = JSON.parse(fundamentalHiddenStats);
          
          if (fundamentalStats.length > 0) {
            const fundamentalPreferences = {
              tickerBasicsId,
              fundamentalIndicatorsToHide: fundamentalStats
            };

            const response = await fetch('/api/v1/user-ticker/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(fundamentalPreferences),
            });

            if (!response.ok) {
              console.error(`Failed to save fundamental preferences for ${ticker}:`, await response.text());
            } else {
              console.log(`Successfully saved fundamental preferences for ${ticker}`);
            }
          }
        }

        // Save analyst preferences if they exist
        if (analystHiddenStats) {
          const analystStats = JSON.parse(analystHiddenStats);
          
          if (analystStats.length > 0) {
            const analystPreferences = {
              tickerBasicsId,
              analystIndicatorsToHide: analystStats
            };

            const response = await fetch('/api/v1/user-ticker/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(analystPreferences),
            });

            if (!response.ok) {
              console.error(`Failed to save analyst preferences for ${ticker}:`, await response.text());
            } else {
              console.log(`Successfully saved analyst preferences for ${ticker}`);
            }
          }
        }

      } catch (error) {
        console.error(`Failed to save preferences for ticker ${ticker}:`, error);
        // Continue with other tickers even if one fails
      }
    }
  };

  // Helper function to clear ticker preferences from sessionStorage
  const clearTickerPreferencesFromSession = (tickers: string[]) => {
    for (const ticker of tickers) {
      sessionStorage.removeItem(`stocktable-ticker-technicals-hidden-statistics-${ticker}`);
      sessionStorage.removeItem(`stocktable-ticker-technicals-hidden-columns-${ticker}`);
      sessionStorage.removeItem(`stocktable-ticker-fundamentals-hidden-statistics-${ticker}`);
      sessionStorage.removeItem(`stocktable-ticker-analyst-views-hidden-statistics-${ticker}`);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      <Header showSignUp={false} />
      
      <main className="flex-grow flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Notice bar for save action */}
          {isFromSaveAction && (
            <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md p-3 flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-highlight-gold" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-primary dark:text-text-primary`}>
                Register an account to save your configurations.
              </p>
            </div>
          )}

          {/* Notice bar for new list creation */}
          {isFromNewList && (
            <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md p-3 flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-highlight-gold" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
              <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-primary dark:text-text-primary`}>
                Register an account to create and manage your stock lists.
              </p>
            </div>
          )}

          {/* Notice bar for Sign Up button */}
          {isFromSignUp && !isFromPublicList && (
            <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md p-3 flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-highlight-gold" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-primary dark:text-text-primary`}>
                Register an account to save your current stock list and preferences.
              </p>
            </div>
          )}
          
          <div>
            <h2 className={`mt-6 text-center ${getPageTitleSizeClass(fontSize)} font-bold tracking-tight text-text-light-primary dark:text-text-primary`}>
              Create your Stock Table account
            </h2>
            <p className={`mt-2 text-center ${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
              For investors who think in tables, not charts.
            </p>
          </div>
          
          <div className="bg-surface-light dark:bg-background-card p-8 rounded-lg shadow-lg border border-border-light dark:border-border-color">
            <div className="mb-6 w-full">
              <GoogleSignInButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="signup_with"
                disabled={isGoogleLoading}
              />
            </div>
            
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border-light dark:border-border-color"></div>
                </div>
                <div className={`relative flex justify-center ${getSmallTextSizeClass(fontSize)}`}>
                  <span className="px-2 bg-surface-light dark:bg-background-card text-text-light-secondary dark:text-text-secondary">Or continue with email</span>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="mt-6 space-y-6" noValidate>
              <div>
                <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary`} htmlFor="email">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    autoComplete="email"
                    className={`w-full p-3 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                      errors.email 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="email"
                    name="email"
                    required
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                {errors.email && (
                  <p className={`mt-1 ${getSmallTextSizeClass(fontSize)} text-red-600 dark:text-red-400`}>{errors.email}</p>
                )}
              </div>
              
              <div>
                <label className={`block ${getSmallTextSizeClass(fontSize)} font-medium text-text-light-secondary dark:text-text-secondary`} htmlFor="password">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    autoComplete="current-password"
                    className={`w-full p-3 pr-12 bg-white dark:bg-background border rounded-md text-text-light-primary dark:text-text-primary placeholder-text-light-secondary dark:placeholder-text-secondary focus:outline-none focus:ring-1 ${
                      errors.password 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                        : 'border-border-light dark:border-border-color focus:border-primary dark:focus:border-highlight-gold focus:ring-primary dark:focus:ring-highlight-gold'
                    }`}
                    id="password"
                    name="password"
                    required
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 px-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      // Eye with diagonal slash overlay
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        {/* Base eye (same as show icon) */}
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                        {/* Diagonal slash overlay */}
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4L20 20"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-text-light-secondary dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className={`mt-1 ${getSmallTextSizeClass(fontSize)} text-red-600 dark:text-red-400`}>{errors.password}</p>
                )}
              </div>
              
              <div>
                <button
                  className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm ${getTextSizeClass(fontSize)} font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </form>
          </div>
          
          <p className={`mt-10 text-center ${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
            By creating an account, you agree to our{' '}
            <Link 
              to="/terms-of-service" 
              state={{ fromSignUp: true }}
              className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors"
            >
              Terms of Service
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
};

export default SignUp;
